import React, { useState, useEffect, useRef } from "react";
import { Container, Row, Col, Card, Button, Form, ProgressBar } from "react-bootstrap";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import 'bootstrap/dist/css/bootstrap.min.css';
import { getFirestore,  doc, getDoc, getDocs, setDoc , collection, addDoc, onSnapshot, query, orderBy } from "firebase/firestore";
import { db } from "./firebase";   // ✅ use the initialized instance


// const db = getFirestore();

export default function App() {
  const DAILY_TARGET = 120; // 2 hours = 120 minutes
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [newTodo, setNewTodo] = useState("");
  const [history, setHistory] = useState([]);
  const [timerRunning, setTimerRunning] = useState(false);
  const timerRef = useRef(null);
  const [activeProjectTimer, setActiveProjectTimer] = useState(null); // currently active project
  const [loading, setLoading] = useState(true); // ✅ track loading

  // // Save projects & history
  // useEffect(() => { localStorage.setItem("projects", JSON.stringify(projects)); }, [projects]);
  // useEffect(() => { localStorage.setItem("history", JSON.stringify(history)); }, [history]);

  // Reset daily timeSpent at midnight
  // useEffect(() => {
  //   const today = new Date().toDateString();
  //   const lastReset = localStorage.getItem("lastReset");
  //   if (lastReset !== today) {
  //     setProjects(prev => prev.map(p => ({ ...p, timeSpent: 0 })));
  //     localStorage.setItem("lastReset", today);
  //   }
  // }, []);

  // Save other data (projects, todos, etc.) immediately
  useEffect(() => {
    if (loading) return; // ✅ block until Firestore is ready
    if (!projects.length) return;

    const saveProjects = async () => {
      try {
        await setDoc(doc(db, "users", "defaultUser"), { projects }, { merge: true });
        console.log("✅ Projects saved");
      } catch (error) {
        console.error("❌ Error saving projects:", error);
      }
    };
    saveProjects();
  }, [projects, loading]);


    // Save history every 5 min (only if loaded)
    useEffect(() => {
      if (loading) return;
      const interval = setInterval(async () => {
        if (!history.length) return;
        try {
          await setDoc(doc(db, "users", "defaultUser"), { history }, { merge: true });
          console.log("✅ History auto-saved (5 mins)");
        } catch (error) {
          console.error("❌ Error saving history:", error);
        }
      }, 5 * 60 * 1000);
      return () => clearInterval(interval);
    }, [history, loading]);


  useEffect(() => {
    const handleBeforeUnload = async () => {
      if (projects.length) {
        await setDoc(doc(db, "users", "defaultUser"), { projects, history });
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [projects, history]);


//  useEffect(() => {
//   const loadData = async () => {
//       try {
//         const docRef = doc(db, "users", "defaultUser");
//         const snapshot = await getDoc(docRef);
//         if (snapshot.exists()) {
//           const data = snapshot.data();
//           if (data.projects) setProjects(data.projects);
//           if (data.history) setHistory(data.history);
//           console.log("✅ Data loaded from Firestore");
//         } else {
//           console.log("⚠️ No Firestore data found, using defaults");
//         }
//       } catch (error) {
//         console.error("❌ Error loading data:", error);
//       }
//     };
//     loadData();
//   }, []);

    // Load data from Firestore
      useEffect(() => {
        const docRef = doc(db, "users", "defaultUser");

        const unsub = onSnapshot(docRef, (snapshot) => {
          if (snapshot.exists()) {
            const data = snapshot.data();
            if (data.projects) {
              setProjects(data.projects);

              // ensure selectedProject is valid
              if (!data.projects.find(p => p.name === selectedProject)) {
                setSelectedProject(data.projects[0]?.name || null);
              }
            }
            if (data.history) setHistory(data.history);
            console.log("✅ Data synced from Firestore");
          } else {
            console.log("⚠️ No Firestore data found, using defaults");
            setProjects([{ name: "priests.africa", timeSpent: 0, todos: [] }]);
            setSelectedProject("priests.africa");
          }
          setLoading(false); // ✅ finished loading
        });

        return () => unsub();
      }, []);




  // Timer: increments every second
  useEffect(() => {
    if (timerRunning) {
      timerRef.current = setInterval(() => {
        setProjects(prev =>
          prev.map(p =>
            p.name === selectedProject ? { ...p, timeSpent: p.timeSpent + 1 / 60 } : p
          )
        );

        const today = new Date().toISOString().split("T")[0];
        setHistory(prev => [...prev, { date: today, project: selectedProject, minutes: 1 / 60 }]);
      }, 1000); // 1 second
    } else {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [timerRunning, selectedProject]);


  useEffect(() => {
    if (activeProjectTimer) {
      let seconds = 0;

      timerRef.current = setInterval(() => {
        seconds++;

        // Update UI every second (so user sees live time)
        setProjects(prev =>
          prev.map(p =>
            p.name === activeProjectTimer
              ? { ...p, timeSpent: p.timeSpent + 1 / 60 } // add 1 second in hours
              : p
          )
        );

        // Only commit to history every 60 seconds
        if (seconds % 60 === 0) {
          const today = new Date().toISOString().split("T")[0];
          setHistory(prev => [
            ...prev,
            { date: today, project: activeProjectTimer, minutes: 1 } // store 1 full minute
          ]);
        }
      }, 1000);
    } else {
      clearInterval(timerRef.current);
    }

    return () => clearInterval(timerRef.current);
  }, [activeProjectTimer]);




  const addTodo = () => {
    if (!newTodo.trim()) return;
    const today = new Date().toLocaleDateString(); // or use toISOString() if you want full format
    setProjects(prev =>
      prev.map(p =>
        p.name === selectedProject
          ? { ...p, todos: [...p.todos, { text: newTodo, done: false, createdAt: today }] }
          : p
      )
    );
    setNewTodo("");
  };

  const toggleTodo = index => {
    setProjects(prev =>
      prev.map(p =>
        p.name === selectedProject
          ? { ...p, todos: p.todos.map((t, i) => i === index ? { ...t, done: !t.done } : t) }
          : p
      )
    );
  };

  // Delete a todo
  const deleteTodo = (index) => {
    setProjects((prev) =>
      prev.map((p) =>
        p.name === selectedProject
          ? {
              ...p,
              todos: p.todos.filter((_, i) => i !== index),
            }
          : p
      )
    );
  };

  const addProject = () => {
    const name = prompt("Enter new project name:");
    if (name && !projects.find(p => p.name === name)) {
      setProjects([...projects, { name, timeSpent: 0, todos: [] }]);
      setSelectedProject(name);
    }
  };

  // Last 7 days history
  const historyData = (() => {
    const today = new Date();
    const last7Days = [...Array(7)].map((_, i) => {
      const d = new Date(today); d.setDate(today.getDate() - (6 - i));
      return d.toISOString().split("T")[0];
    });
    return last7Days.map(date => {
      const total = history
        .filter(h => h.project === selectedProject && h.date === date)
        .reduce((sum, h) => sum + h.minutes, 0);
      return { date, minutes: total };
    });
  })();

  const currentProject = projects.find(p => p.name === selectedProject) || { name: "", timeSpent: 0, todos: [] };
  const owed = Math.max(0, DAILY_TARGET - currentProject.timeSpent);

  if (loading) {
    return <div className="text-center p-5">⏳ Loading your data...</div>;
  }


  return (
    <Container fluid className="py-5" style={{ backgroundColor: "#f7f9fc", minHeight: "100vh" }}>
      <Row className="justify-content-center">
        <Col md={8} lg={6}>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h3 className="fw-bold text-success mb-0">⏳ Orunmila's Timer</h3>
            <Button variant="outline-success" size="sm" onClick={addProject}>+ Add Project</Button>
          </div>

          <Form.Select
            value={selectedProject}
            onChange={(e) => {
              // Pause current timer automatically
              setActiveProjectTimer(null);
              setSelectedProject(e.target.value);
            }}
            className="mb-4"
          >
            {projects.map((p) => (
              <option key={p.name}>{p.name}</option>
            ))}
          </Form.Select>

          <Card className="shadow-sm rounded-4 mb-4">
            <Card.Body>
              <h5 className="fw-bold mb-3">Daily Progress ({selectedProject})</h5>
              <ProgressBar now={(currentProject.timeSpent / DAILY_TARGET) * 100}
                           label={`${Math.floor(currentProject.timeSpent)}m / ${DAILY_TARGET}m`}
                           className="mb-3"/>
              <Button
                variant={activeProjectTimer === selectedProject ? "danger" : "success"}
                onClick={() =>
                  setActiveProjectTimer(prev =>
                    prev === selectedProject ? null : selectedProject
                  )
                }
                className="mb-2"
              >
                {activeProjectTimer === selectedProject ? "Pause" : "Resume"}
              </Button>

              {owed > 0 && <div className="text-secondary small mt-2">
                Time remaining: {Math.max(0, (DAILY_TARGET - currentProject.timeSpent).toFixed(1))} minutes
              </div>}
            </Card.Body>
          </Card>

          <Card className="shadow-sm rounded-4 mb-4">
            <Card.Body>
              <h5 className="fw-bold mb-3">TODOs</h5>
              <div className="mb-3">
                <Form.Control type="text" value={newTodo} onChange={e => setNewTodo(e.target.value)}
                    placeholder="Add new task to complete..." onKeyDown={e => e.key === "Enter" && addTodo()}/>
              </div>
              <ul className="list-unstyled">
                {currentProject.todos.map((t, i) => (
                  
                  <li key={i} className="d-flex justify-content-between align-items-center mb-1"
                      style={{
                        cursor: "pointer",
                        textDecoration: t.done ? "line-through" : "none",
                        color: t.done ? "#6c757d" : "inherit",
                      }}
                        >
                      <Button
                        size="sm"
                        variant={t.done ? "success" : "outline-secondary"}
                        onClick={() => toggleTodo(i)}
                        style={{ width: "20px", height: "20px", padding: 0 }}
                      >
                        {t.done ? "✓" : ""}
                      </Button>
                      <div className="ms-2 flex-grow-1">
                      <span onClick={() => toggleTodo(i)}>{t.text}</span>
                        <small className="d-block text-muted" style={{ fontSize: "0.7rem" }}>
                          {t.createdAt}
                        </small>
                      </div>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => deleteTodo(i)}
                        >
                          Delete
                        </Button>

                      </li>
                ))}
              </ul>
            </Card.Body>
          </Card>

          <Card className="shadow-sm rounded-4">
            <Card.Body>
              <h5 className="fw-bold mb-3">Last 7 Days ({selectedProject})</h5>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={historyData}>
                  <CartesianGrid strokeDasharray="3 3"/>
                  <XAxis dataKey="date"/>
                  <YAxis/>
                  <Tooltip/>
                  <Bar dataKey="minutes" fill="#28a745" radius={[8, 8, 0, 0]}/>
                </BarChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}
